import React, { useState, KeyboardEvent, useRef } from "react";
import {
  Box,
  Chip,
  TextField,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  Tooltip,
} from "@mui/material";
import PostAddIcon from "@mui/icons-material/PostAdd";
import CollapsibleFormElement from "../../common/collapsible-form-element";

type WordsToAvoidProps = {
  words: string[];
  onChange: (words: string[]) => void;
};

const WordsToAvoid: React.FC<WordsToAvoidProps> = ({ words, onChange }) => {
  const [inputValue, setInputValue] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Convert comma-separated string to array of words
  const wordsArray = Array.isArray(words)
    ? words.map((word) => word.trim()).filter((word) => word !== "")
    : [];

  // Add a new word to the list
  const addWord = (word: string) => {
    if (!word.trim()) return;

    const newWord = word.trim();
    if (!wordsArray.includes(newWord)) {
      const newWords = [...wordsArray, newWord];
      onChange(newWords);
    }

    setInputValue("");
  };

  // Remove a word from the list
  const removeWord = (wordToRemove: string) => {
    const filteredWords = wordsArray.filter((word) => word !== wordToRemove);

    onChange(filteredWords);
  };

  // Clear all words
  const clearAllWords = () => {
    onChange([]);
  };

  // Handle key press in the input field
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addWord(inputValue);
    }
  };

  // Handle bulk insert of words
  const handleBulkInsert = () => {
    if (!bulkText.trim()) {
      setModalOpen(false);
      return;
    }

    // Split by newlines and/or commas
    const newWords = bulkText
      .split(/[,\n]+/)
      .map((word) => word.trim())
      .filter((word) => word !== "");

    // Combine existing and new words, removing duplicates
    const combinedWords = [...new Set([...wordsArray, ...newWords])];

    onChange(combinedWords);

    setBulkText("");
    setModalOpen(false);
  };

  // Focus the input when clicking on the paper container
  const handlePaperClick = () => {
    inputRef.current?.focus();
  };

  return (
    <CollapsibleFormElement title="Words to Avoid">
      <Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mb: 1, display: "block" }}
        >
          Press Enter or comma to add a word. Use the icon{" "}
          <PostAddIcon fontSize="inherit" /> for multiple entries.
        </Typography>
      </Box>

      <Paper
        sx={{
          p: 1,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          border: "1px solid",
          borderColor: "divider",
          minHeight: 56,
          cursor: "text",
        }}
        onClick={handlePaperClick}
      >
        {wordsArray.map((word) => (
          <Chip
            key={word}
            label={word}
            size="small"
            onDelete={() => removeWord(word)}
            sx={{ m: 0.5 }}
            color="primary"
            variant="outlined"
          />
        ))}

        <TextField
          inputRef={inputRef}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            // Add the word when the input loses focus
            if (inputValue.trim()) {
              addWord(inputValue);
            }
          }}
          placeholder={
            wordsArray.length === 0 ? "Enter words to avoid" : ""
          }
          variant="standard"
          sx={{
            flex: "1 0 100px",
            m: 0.5,
            input: { p: 0.5 },
          }}
          slotProps={{
            input: {
              disableUnderline: true,
            },
          }}
        />
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
        <Box>
          {wordsArray.length > 0 && (
            <Tooltip title="Clear all">
              <Button
                color="error"
                onClick={clearAllWords}
                size="small"
                variant="outlined"
              >
                Reset
              </Button>
            </Tooltip>
          )}
        </Box>
        <Box sx={{ display: "flex" }}>
          <Tooltip title="Bulk Insert">
            <Button
              color="primary"
              onClick={() => setModalOpen(true)}
              size="small"
              variant="outlined"
            >
              Add phrases in bulk
            </Button>
          </Tooltip>
        </Box>
      </Box>

      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Bulk Insert</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Enter multiple words separated by commas or on different lines.
          </Typography>
          <FormControl fullWidth>
            <TextField
              multiline
              rows={6}
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              placeholder="Example:\nword1\nword2\nword3\n\nor: word1, word2, word3"
              variant="outlined"
              autoFocus
              fullWidth
            />
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Cancel</Button>
          <Button onClick={handleBulkInsert} variant="contained">
            Insert
          </Button>
        </DialogActions>
      </Dialog>
    </CollapsibleFormElement>
  );
};

export default WordsToAvoid;
